import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	type DialogProps,
	Divider,
	Input,
	List,
	Typography,
	useTheme,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { makeStyles } from "@mui/styles";
import { type Theme } from "@mui/material/styles";
import type { GridApi } from "ag-grid-community";
import { useUploadFiles } from "./hooks/useUploadFiles";
import { useEffect, useState } from "react";
import { useTemplatesContext } from "@/pages/templates/context/templatesContext";
import { useTemplateAttachmentGridActionsAndButtons } from "../../hooks/useTemplateAttachmentsGridActionsAndButtons";
import { ErrorModal, MESApiService } from "@dexteel/mesf-core";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { linkTemplateAttachments } from "@/pages/templates/repositories/TemplatesRepositorie";
import type { TemplateAttachment } from "../../../../models/TemplateAttachment";

const useStyles = makeStyles((theme: Theme) => ({
	root: {
		"& .input-group-text": {
			display: "none",
		},
		"& .MuiDialogContent-dividers": {
			padding: 0,
		},
	},
	formControl: {
		margin: theme.spacing(2),
		minWidth: 120,
	},
}));

type Props = {
	TemplateId: number;
	gridRef: GridApi | null;
	searchTemplateAttachment: () => void;
	setRemoveAttachmentError: (err: string) => void;
	setListMessage: (err: string) => void | null;
	setShowLinkResourcesToAssetDialog: (show: boolean) => void;
	setNewUploadedAttachments: (attachments: TemplateAttachment[]) => void;
};

export const UploadFilePage = ({
	TemplateId,
	gridRef,
	searchTemplateAttachment,
	setRemoveAttachmentError,
	setListMessage,
	setShowLinkResourcesToAssetDialog,
	setNewUploadedAttachments,
}: Props) => {
	const {
		handleFileSelect,
		handleDragOver,
		handleDrop,
		handleCancel,
		setIsDragging,
		setSelectedFiles,
		selectedFiles,
	} = useUploadFiles();

	const classes = useStyles();
	const theme = useTheme();
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(false);
	const [fullWidth, setFullWidth] = useState(true);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("sm");
	const [attachmentsToAdd, setAttachmentsToAdd] = useState([]);
	const {
		state: { assetAttachments },
		actions: { setUploadFileDialog },
	} = useTemplatesContext();

	const { refresh } = useTemplateAttachmentGridActionsAndButtons({
		gridRef,
		searchTemplateAttachment,
		setRemoveAttachmentError,
		setListMessage,
	});
	const [
		linkAssetAttachmentsLoadingError,
		setLinkAssetAttachmentsLoadingError,
	] = useState<string>("");
	const [progressMap, setProgressMap] = useState<Record<string, number>>({});

	const handleRemoveFile = (fileNameToRemove: string) => {
		const newArr = selectedFiles.filter(
			(file: any) => file.name !== fileNameToRemove,
		);
		setSelectedFiles(newArr);
	};

	const api = new MESApiService();

	const handleProgress = (event: any) => {
		if (
			event &&
			event.target &&
			event.target.files &&
			event.target.files.length > 0
		) {
			Array.from<File>(event.target.files).forEach((file: File) => {
				const fileName = file.name;

				const reader = new FileReader();

				reader.onloadend = () => {
					setProgressMap((prevProgressMap) => ({
						...prevProgressMap,
						[fileName]: 100,
					}));
				};

				reader.onprogress = (e: ProgressEvent<FileReader>) => {
					if (e.lengthComputable) {
						const fileProgress = Math.round((e.loaded / e.total) * 100);
						setProgressMap((prevProgressMap) => ({
							...prevProgressMap,
							[fileName]: fileProgress,
						}));
					}
				};

				reader.readAsDataURL(file);
			});
		}
	};

	const handleDropWithProgress = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();

		const files = e.dataTransfer.files;

		if (files.length > 0) {
			Array.from(files).forEach((file: File) => {
				const fileName = file.name;

				const reader = new FileReader();

				reader.onloadend = () => {
					setProgressMap((prevProgressMap) => ({
						...prevProgressMap,
						[fileName]: 100,
					}));
				};

				reader.onprogress = (progressEvent: ProgressEvent<FileReader>) => {
					if (progressEvent.lengthComputable) {
						const fileProgress = Math.round(
							(progressEvent.loaded / progressEvent.total) * 100,
						);
						setProgressMap((prevProgressMap) => ({
							...prevProgressMap,
							[fileName]: fileProgress,
						}));
					}
				};

				reader.readAsDataURL(file);
			});

			handleDrop(e);
			setIsDragging(false);
		}
	};

	const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (selectedFiles.length === 0) return;

		setIsSubmitLoading(true);
		try {
			const uploadedFiles = await api.uploadFiles(selectedFiles, "Attachments");
			const formattedAttachments = uploadedFiles.result.map((att: any) => ({
				UploadFileId: att.UploadFileId,
				FileName: att.FileName,
				IsAssetResource: false,
			}));

			const res = await linkTemplateAttachments(
				TemplateId,
				formattedAttachments,
			);

			if (res.ok) {
				const newAttachments = formattedAttachments.filter(
					(newAtt: any) =>
						!assetAttachments.some(
							(assetAtt: any) => assetAtt.UploadFileId === newAtt.UploadFileId,
						),
				);

				if (newAttachments.length > 0) {
					setNewUploadedAttachments(newAttachments);
					setShowLinkResourcesToAssetDialog(true);
				}

				refresh();
				setUploadFileDialog(false);
			} else {
				setLinkAssetAttachmentsLoadingError(res.message);
			}
		} catch (error) {
			setLinkAssetAttachmentsLoadingError(
				"An error occurred while uploading and linking attachments",
			);
		} finally {
			setIsSubmitLoading(false);
			setSelectedFiles([]);
		}
	};

	useEffect(() => {
		setIsSubmitDisabled(selectedFiles.length === 0);
	}, [selectedFiles]);

	useEffect(() => {
		if (attachmentsToAdd?.length >= 1) {
			setIsSubmitLoading(true);
			const linkAttachments = async () => {
				try {
					const formattedAttachments = attachmentsToAdd.map(
						(att: TemplateAttachment) => ({
							UploadFileId: att.UploadFileId,
							FileName: att.FileName,
							IsAssetResource: false,
						}),
					);

					const res = await linkTemplateAttachments(
						TemplateId as number,
						formattedAttachments,
					);

					if (res.ok) {
						setIsSubmitLoading(false);
					} else {
						setLinkAssetAttachmentsLoadingError(res.message);
						setIsSubmitLoading(false);
					}
				} catch (error) {
					setLinkAssetAttachmentsLoadingError(
						"An error occurred while linking attachments",
					);
					setIsSubmitLoading(false);
				}
			};

			linkAttachments();
		} else {
			setIsSubmitDisabled(true);
		}
	}, [attachmentsToAdd, TemplateId]);

	useEffect(() => {
		setIsSubmitDisabled(selectedFiles.length === 0);
	}, [selectedFiles]);

	useEffect(() => {
		if (attachmentsToAdd?.length >= 1) {
			setIsSubmitLoading(true);
			const linkAttachments = async () => {
				try {
					const formattedAttachments = attachmentsToAdd.map(
						(att: TemplateAttachment) => ({
							UploadFileId: att.UploadFileId,
							FileName: att.FileName,
							IsAssetResource: false,
						}),
					);

					const res = await linkTemplateAttachments(
						TemplateId as number,
						formattedAttachments,
					);

					if (res.ok) {
						setIsSubmitLoading(false);
					} else {
						setLinkAssetAttachmentsLoadingError(res.message);
						setIsSubmitLoading(false);
					}
				} catch (error) {
					setLinkAssetAttachmentsLoadingError(
						"An error occurred while linking attachments",
					);
					setIsSubmitLoading(false);
				}
			};

			linkAttachments();
		} else {
			setIsSubmitDisabled(true);
		}
	}, [attachmentsToAdd, TemplateId]);

	return (
		<>
			<Dialog
				onClose={() => handleCancel()}
				fullWidth={fullWidth}
				maxWidth={maxWidth}
				open={true}
				className={classes.root}
			>
				<form onSubmit={handleFormSubmit}>
					<Grid2
						container
						style={{ padding: "15px 0 5px", height: "30%", overflow: "hidden" }}
					>
						<Grid2
							container
							style={{
								alignItems: "center",
								marginTop: 0,
								padding: 0,
								margin: 0,
							}}
							spacing={2}
							size={{ md: 12, xs: 12 }}
						>
							<Grid2
								container
								alignItems="center"
								style={{ padding: "8px 20px 8px" }}
								size={{ md: 12, xs: 12 }}
							>
								<Grid2 size={{ md: 12, xs: 12 }}>
									<div
										className={classes.formControl}
										onDragOver={(e) => {
											handleDragOver(e);
										}}
										onDragEnter={() => setIsDragging(true)}
										onDragLeave={() => setIsDragging(false)}
										onDragEnd={() => setIsDragging(false)}
										onDrop={(e) => {
											handleDrop(e);
											setIsDragging(false);
											handleDropWithProgress(e);
										}}
										style={{
											width: "100%",
											margin: "0",
											border: `2px dashed ${theme.palette.primary.main}`,
											borderRadius: "5px",
											padding: "20px",
											textAlign: "center",
											cursor: "pointer",
										}}
										onClick={() =>
											document.getElementById("fileInput")?.click()
										}
									>
										<Typography>Drag & Drop Files here</Typography>
										<Typography>or</Typography>
										<Typography>Click Here</Typography>
										<Input
											type="file"
											onChange={(event: any) => {
												handleFileSelect(event);
												handleProgress(event);
											}}
											style={{ display: "none" }}
											id="fileInput"
											inputProps={{ multiple: true }}
										/>
									</div>
								</Grid2>
							</Grid2>

							{selectedFiles.length >= 1 && (
								<Grid2
									container
									style={{ marginTop: "10px", marginLeft: 20, padding: "0" }}
									size={{ md: 12, xs: 12 }}
								>
									<Grid2
										style={{
											justifyContent: "flex-start !important",
											alignItems: "flex-start",
										}}
										size={{ md: 12, xs: 12 }}
									>
										<Typography style={{ fontWeight: "bold" }}>
											Document Selected:
										</Typography>

										<List
											style={{
												height: "20vh",
												margin: "10px 20px 20px 0",
												padding: "10px",
												overflow: "auto",
												background: "#FCFCFC",
											}}
										>
											{selectedFiles.map((file: any) => (
												<>
													<div
														key={`file-${file.name}`}
														style={{
															textAlign: "left",
															display: "flex",
															alignItems: "center",
															padding: 0,
															margin: "5px 0",
														}}
													>
														<span
															style={{ marginRight: 5, cursor: "pointer" }}
															onClick={() => handleRemoveFile(file.name)}
														>
															<DeleteForeverIcon />
														</span>
														<p
															style={{
																margin: "5px 0",
																wordBreak: "break-all",
															}}
														>
															{file.name} ({(file.size / 1024).toFixed(2)} KB)
														</p>
													</div>
													{progressMap[file.name] > 0 && (
														<div
															style={{
																width: "100%",
																height: 20,
																backgroundColor: "#f2f2f2",
																borderRadius: 5,
															}}
														>
															<div
																style={{
																	width: `${progressMap[file.name]}%`,
																	height: "100%",
																	backgroundColor: `rgba(63, 81, 181, ${
																		progressMap[file.name] / 100
																	})`,
																	borderRadius: "inherit",
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "center",
																	color: "white",
																	fontWeight: "bold",
																}}
															>
																<span style={{ fontSize: "12px" }}>{`${
																	progressMap[file.name]
																}%`}</span>
															</div>
														</div>
													)}
												</>
											))}
										</List>
									</Grid2>
								</Grid2>
							)}
							<Divider
								variant="middle"
								style={{
									margin: "10px 0",
									width: "150%",
									marginLeft: "-100px",
								}}
							/>
							<Grid2 justifyContent="flex-end" size={{ md: 12, xs: 12 }}>
								<DialogActions style={{ padding: "0px 10px " }}>
									<Grid2
										container
										justifyContent="flex-end"
										style={{ margin: 0 }}
									>
										<Grid2
											style={{ margin: 0, padding: "2px 0 8px 0" }}
											size={{ md: 3, xs: 4 }}
										>
											<Button
												fullWidth
												variant="contained"
												color="inherit"
												onClick={() => setUploadFileDialog(false)}
											>
												Cancel
											</Button>
										</Grid2>
										<Grid2
											style={{
												margin: 0,
												padding: "2px 0 8px 0",
												marginLeft: "20px",
											}}
											size={{ md: 3, xs: 4 }}
										>
											<Button
												fullWidth
												startIcon={
													isSubmitLoading && <CircularProgress size="1rem" />
												}
												disabled={isSubmitLoading || isSubmitDisabled}
												variant="contained"
												color="primary"
												type="submit"
											>
												Save
											</Button>
										</Grid2>
									</Grid2>
								</DialogActions>
							</Grid2>
						</Grid2>
					</Grid2>
				</form>
			</Dialog>
			<ErrorModal
				error={linkAssetAttachmentsLoadingError}
				onHide={() => setLinkAssetAttachmentsLoadingError("")}
			/>
		</>
	);
};
